import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  captureClientConsents,
  fetchClientConsents,
  fetchConsentsOverview,
  requestClientConsents,
} from '@/api/consents/consentsApi';
import type {
  ConsentCaptureItem,
  ConsentOverview,
  ConsentOverviewFilter,
  ConsentStatusItem,
} from '@/api/consents/consentsApi.types';
import {
  buildClientConsentsQueryKey,
  buildClientQueryKey,
  buildConsentsOverviewQueryKey,
} from '@/constants/queryKeys';

export function useClientConsentsQuery(
  clientId: string
): UseQueryResult<ConsentStatusItem[], Error> {
  return useQuery({
    queryKey: buildClientConsentsQueryKey(clientId),
    queryFn: function loadConsents() {
      return fetchClientConsents(clientId);
    },
  });
}

export function useConsentsOverviewQuery(
  page: number,
  search: string,
  overviewFilter: ConsentOverviewFilter
): UseQueryResult<ConsentOverview, Error> {
  return useQuery({
    queryKey: buildConsentsOverviewQueryKey(page, search, overviewFilter),
    queryFn: function loadOverview() {
      return fetchConsentsOverview(page, search, overviewFilter);
    },
    placeholderData: keepPreviousData,
  });
}

export function useRequestClientConsentsMutation(
  clientId: string
): UseMutationResult<void, Error, void> {
  return useMutation({
    mutationFn: function request() {
      return requestClientConsents(clientId);
    },
  });
}

export function useCaptureClientConsentsMutation(
  clientId: string
): UseMutationResult<ConsentStatusItem[], Error, ConsentCaptureItem[]> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: function capture(items: ConsentCaptureItem[]) {
      return captureClientConsents(clientId, items);
    },
    onSuccess: function done() {
      queryClient.invalidateQueries({ queryKey: buildClientConsentsQueryKey(clientId) });
      queryClient.invalidateQueries({ queryKey: buildClientQueryKey(clientId) });
    },
  });
}
